import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    CheckCircle, MessageSquare, Clock, AlertTriangle, X, Calendar,
    ChevronRight, Target, HelpCircle, Flame
} from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { toast } from 'sonner'

interface Task {
    id: number
    subject: string
    title: string
    type: string
    duration: string
    status: string
    priority: string
    proof: string
}

export function DailyPlanView() {
    const [viewMode, setViewMode] = useState<'today' | 'history'>('today')
    const [selectedHistoryDate, setSelectedHistoryDate] = useState<string | null>(null)

    const [tasks, setTasks] = useState<Task[]>([
        { id: 1, subject: 'Physics', title: 'Thermodynamics Chapter 4 Reading', type: 'Reading', duration: '1h', status: 'pending', priority: 'High', proof: '' },
        { id: 2, subject: 'Chemistry', title: 'Solve 50 MCQs from Organic Chemistry', type: 'MCQ', duration: '1.5h', status: 'completed', priority: 'High', proof: 'Attached Note' },
        { id: 3, subject: 'Biology', title: 'Human Physiology Revision', type: 'Revision', duration: '2h', status: 'pending', priority: 'Medium', proof: '' }
    ])

    const [selectedTask, setSelectedTask] = useState<number | null>(null)
    const [proofNote, setProofNote] = useState('')
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false)
    const [summaryData, setSummaryData] = useState({
        totalHours: '',
        physics: '',
        chemistry: '',
        biology: '',
        workDescription: '',
        challenges: ''
    })

    const completedCount = tasks.filter(t => t.status === 'completed').length
    const progressPercent = Math.round((completedCount / tasks.length) * 100)

    const subjectColors: Record<string, string> = {
        Physics: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        Chemistry: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        Biology: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    }

    const handleTaskClick = (id: number) => {
        const task = tasks.find(t => t.id === id)
        if (task?.status === 'completed') {
            setTasks(tasks.map(t => t.id === id ? { ...t, status: 'pending', proof: '' } : t))
        } else {
            setSelectedTask(id)
            setProofNote('')
        }
    }

    const confirmTaskCompletion = () => {
        if (selectedTask) {
            setTasks(tasks.map(t => t.id === selectedTask ? { ...t, status: 'completed', proof: proofNote } : t))
            setSelectedTask(null)
            toast.success("Task marked as completed!")
        }
    }

    const handleRequestHelp = () => {
        toast.info("Help request sent to Dr. Sarah!")
    }

    const handleSummarySubmit = (e: React.FormEvent) => {
        e.preventDefault()
        toast.success("Day Summary Submitted Successfully!")
        setIsSummaryModalOpen(false)
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header with Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">
                    {viewMode === 'today' ? "Today's Plan" : "Plan History"}
                </h2>
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    <button
                        onClick={() => setViewMode('today')}
                        className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${viewMode === 'today' ? 'bg-white dark:bg-slate-700 shadow text-primary-blue' : 'text-slate-500'}`}
                    >
                        Today
                    </button>
                    <button
                        onClick={() => setViewMode('history')}
                        className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${viewMode === 'history' ? 'bg-white dark:bg-slate-700 shadow text-primary-blue' : 'text-slate-500'}`}
                    >
                        History
                    </button>
                </div>
            </div>

            {viewMode === 'today' ? (
                <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
                    {/* Main Task Area */}
                    <div className="lg:col-span-2 space-y-4 md:space-y-6">
                        {/* Target Header Card */}
                        <GlassmorphicCard className="p-4 md:p-5 relative overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br from-primary-blue/20 to-accent-gold/10 blur-2xl" />
                            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-gradient-to-br from-primary-blue to-blue-600 rounded-xl text-white">
                                        <Target className="h-5 w-5 md:h-6 md:w-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-theme-primary text-base md:text-lg">Today's Target</h3>
                                        <p className="text-xs md:text-sm text-theme-secondary">6 Hours • 3 Tasks</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-full">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">
                                            DS
                                        </div>
                                        <span className="text-sm font-medium text-theme-primary hidden sm:block">Dr. Sarah</span>
                                    </div>
                                    <button
                                        onClick={handleRequestHelp}
                                        className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:text-primary-blue transition-colors"
                                    >
                                        <HelpCircle className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </GlassmorphicCard>

                        {/* Day Progress Bar */}
                        <div className="px-1">
                            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-theme-secondary">Day Progress</span>
                                <span className="font-bold text-theme-primary">{completedCount}/{tasks.length} Tasks</span>
                            </div>
                            <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    className="h-full bg-gradient-to-r from-primary-blue to-accent-gold rounded-full"
                                />
                            </div>
                        </div>

                        {/* Task Cards */}
                        <div className="space-y-3">
                            {tasks.map((task, index) => (
                                <div key={task.id}>
                                    <GlassmorphicCard className={`p-4 transition-all ${task.status === 'completed' ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}>
                                        <div className="flex items-start gap-3 md:gap-4">
                                            {/* Completion Button */}
                                            <button
                                                onClick={() => handleTaskClick(task.id)}
                                                className="mt-0.5 shrink-0 transition-transform hover:scale-110"
                                            >
                                                {task.status === 'completed' ? (
                                                    <motion.div
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500 flex items-center justify-center"
                                                    >
                                                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                                                    </motion.div>
                                                ) : (
                                                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400 hover:border-primary-blue hover:text-primary-blue transition-colors">
                                                        <span className="text-xs font-bold">{index + 1}</span>
                                                    </div>
                                                )}
                                            </button>

                                            {/* Task Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                                    <h4 className={`font-semibold text-sm md:text-base ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-theme-primary'}`}>
                                                        {task.title}
                                                    </h4>
                                                    {task.priority === 'High' && (
                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full w-fit">
                                                            <Flame className="h-3 w-3" /> High Priority
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                                    <span className={`px-2 py-1 rounded-md ${subjectColors[task.subject] || 'bg-slate-100 text-slate-600'}`}>
                                                        {task.subject}
                                                    </span>
                                                    <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-md text-theme-secondary">
                                                        {task.type}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-theme-secondary">
                                                        <Clock className="h-3 w-3" /> {task.duration}
                                                    </span>
                                                </div>
                                                {task.proof && (
                                                    <p className="mt-2 text-xs text-green-600 dark:text-green-400 italic">
                                                        ✓ {task.proof}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </GlassmorphicCard>

                                    {/* Proof Input Expansion */}
                                    <AnimatePresence>
                                        {selectedTask === task.id && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="mt-2 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-800/20"
                                            >
                                                <label className="block text-sm font-medium text-theme-primary mb-2">Add Proof Note (Optional)</label>
                                                <div className="flex flex-col sm:flex-row gap-2">
                                                    <input
                                                        type="text"
                                                        value={proofNote}
                                                        onChange={(e) => setProofNote(e.target.value)}
                                                        placeholder="E.g. Completed pages 45-50"
                                                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={confirmTaskCompletion}
                                                            className="flex-1 sm:flex-none px-4 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600"
                                                        >
                                                            Done
                                                        </button>
                                                        <button
                                                            onClick={() => setSelectedTask(null)}
                                                            className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>

                        {/* Submit Summary Button */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                            <button
                                onClick={handleRequestHelp}
                                className="px-4 py-2.5 text-sm font-medium text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <AlertTriangle className="h-4 w-4" />
                                Request Help
                            </button>
                            <button
                                onClick={() => setIsSummaryModalOpen(true)}
                                className="px-6 py-2.5 bg-gradient-to-r from-primary-blue to-blue-600 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2"
                            >
                                Submit Day Summary
                            </button>
                        </div>
                    </div>

                    {/* Sidebar: Mentor Note + Distribution */}
                    <div className="space-y-4 md:space-y-6">
                        {/* Mentor Note */}
                        <GlassmorphicCard className="p-4 md:p-5 bg-amber-50/50 dark:bg-amber-900/5 border-l-4 border-l-amber-500">
                            <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="h-4 w-4 text-amber-500" />
                                <h3 className="font-bold text-amber-800 dark:text-amber-200 text-sm">Mentor's Note</h3>
                            </div>
                            <p className="text-sm text-amber-700 dark:text-amber-300/80 italic leading-relaxed">
                                "Focus on Thermodynamics numericals today. You've been lagging slightly in Physics this week. Don't skip the revision session!"
                            </p>
                        </GlassmorphicCard>

                        {/* Subject Distribution */}
                        <GlassmorphicCard className="p-4 md:p-5">
                            <h3 className="font-bold text-theme-primary text-sm mb-4">Subject Distribution</h3>
                            <div className="space-y-4">
                                {[
                                    { subject: 'Physics', hours: 2.5, percent: 60, color: 'from-blue-500 to-blue-600' },
                                    { subject: 'Chemistry', hours: 2.0, percent: 40, color: 'from-green-500 to-green-600' },
                                    { subject: 'Biology', hours: 1.5, percent: 30, color: 'from-purple-500 to-purple-600' },
                                ].map((item) => (
                                    <div key={item.subject}>
                                        <div className="flex justify-between text-xs mb-1.5">
                                            <span className="text-theme-secondary">{item.subject}</span>
                                            <span className="font-medium text-theme-primary">{item.hours}h</span>
                                        </div>
                                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${item.percent}%` }}
                                                transition={{ duration: 0.8, delay: 0.2 }}
                                                className={`h-full bg-gradient-to-r ${item.color} rounded-full`}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </GlassmorphicCard>
                    </div>
                </div>
            ) : (
                /* History View */
                <div className="space-y-3">
                    {[
                        { date: 'Oct 24, 2024', status: 'missed', hours: 0, mentor: 'Skipped submission', approved: 0 },
                        { date: 'Oct 23, 2024', status: 'completed', hours: 6.5, mentor: 'Good job! Keep it up.', approved: 6.0 },
                        { date: 'Oct 22, 2024', status: 'completed', hours: 7.2, mentor: 'Excellent consistency. Well done!', approved: 7.0 },
                        { date: 'Oct 21, 2024', status: 'completed', hours: 5.5, mentor: 'Slightly below target. Push harder!', approved: 5.0 },
                    ].map((history, i) => (
                        <GlassmorphicCard key={i} onClick={() => setSelectedHistoryDate(history.date)} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors cursor-pointer">
                            <div className="flex items-start sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-3 md:gap-4">
                                    <div className={`p-2.5 md:p-3 rounded-xl ${history.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-red-100 dark:bg-red-900/30 text-red-600'}`}>
                                        <Calendar className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-theme-primary text-sm md:text-base">{history.date}</h4>
                                        <p className="text-xs text-theme-secondary line-clamp-1">{history.mentor}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 md:gap-6 shrink-0">
                                    <div className="text-right hidden sm:block">
                                        <p className="text-[10px] text-theme-secondary uppercase tracking-wide">Reported</p>
                                        <p className={`font-bold ${history.hours === 0 ? 'text-slate-400' : 'text-theme-primary'}`}>{history.hours}h</p>
                                    </div>
                                    {history.status === 'completed' && (
                                        <div className="text-right hidden sm:block">
                                            <p className="text-[10px] text-theme-secondary uppercase tracking-wide">Approved</p>
                                            <p className="font-bold text-green-600">{history.approved}h</p>
                                        </div>
                                    )}
                                    <span className="font-bold text-theme-primary sm:hidden text-sm">{history.hours}h</span>
                                    <ChevronRight className="h-5 w-5 text-slate-400" />
                                </div>
                            </div>
                        </GlassmorphicCard>
                    ))}
                </div>
            )}

            {/* Day Summary Modal */}
            <AnimatePresence>
                {isSummaryModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                        >
                            <GlassmorphicCard className="w-full max-w-lg p-5 md:p-6 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
                                <button
                                    onClick={() => setIsSummaryModalOpen(false)}
                                    className="absolute top-4 right-4 p-1 text-theme-secondary hover:text-theme-primary rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                                <h3 className="text-lg md:text-xl font-bold text-theme-primary mb-5">Submit Day Summary</h3>

                                <form onSubmit={handleSummarySubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1.5">Total Study Hours</label>
                                        <input
                                            type="number"
                                            step="0.5"
                                            required
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-theme-primary"
                                            placeholder="e.g. 6.5"
                                            value={summaryData.totalHours}
                                            onChange={e => setSummaryData({ ...summaryData, totalHours: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-2">Subject Breakdown (Hours)</label>
                                        <div className="grid grid-cols-3 gap-2 md:gap-3">
                                            <div>
                                                <p className="text-xs text-blue-500 mb-1">Physics</p>
                                                <input type="number" step="0.5" placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700" value={summaryData.physics} onChange={e => setSummaryData({ ...summaryData, physics: e.target.value })} />
                                            </div>
                                            <div>
                                                <p className="text-xs text-green-500 mb-1">Chemistry</p>
                                                <input type="number" step="0.5" placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700" value={summaryData.chemistry} onChange={e => setSummaryData({ ...summaryData, chemistry: e.target.value })} />
                                            </div>
                                            <div>
                                                <p className="text-xs text-purple-500 mb-1">Biology</p>
                                                <input type="number" step="0.5" placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700" value={summaryData.biology} onChange={e => setSummaryData({ ...summaryData, biology: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1.5">What did you complete today?</label>
                                        <textarea
                                            rows={3}
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-theme-primary resize-none"
                                            placeholder="Summarize your progress..."
                                            value={summaryData.workDescription}
                                            onChange={e => setSummaryData({ ...summaryData, workDescription: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1.5">Challenges faced (if any)</label>
                                        <textarea
                                            rows={2}
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-theme-primary resize-none"
                                            placeholder="Difficulty in specific topics?"
                                            value={summaryData.challenges}
                                            onChange={e => setSummaryData({ ...summaryData, challenges: e.target.value })}
                                        />
                                    </div>

                                    <div className="pt-3 flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsSummaryModalOpen(false)}
                                            className="flex-1 py-2.5 text-theme-secondary font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 py-2.5 bg-gradient-to-r from-primary-blue to-blue-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
                                        >
                                            Submit Report
                                        </button>
                                    </div>
                                </form>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* History Detail Modal */}
            <AnimatePresence>
                {selectedHistoryDate && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedHistoryDate(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="w-full max-w-2xl p-5 md:p-6 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
                                <button
                                    onClick={() => setSelectedHistoryDate(null)}
                                    className="absolute top-4 right-4 p-1 text-theme-secondary hover:text-theme-primary rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                                <h3 className="text-lg md:text-xl font-bold text-theme-primary mb-5 flex items-center gap-2">
                                    <Calendar className="h-5 w-5 text-primary-blue" />
                                    {selectedHistoryDate} - Daily Report
                                </h3>

                                {/* Summary Stats */}
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl">
                                        <p className="text-xs text-theme-secondary mb-1">Reported Hours</p>
                                        <p className="text-2xl font-bold text-theme-primary">6.5h</p>
                                    </div>
                                    <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-xl">
                                        <p className="text-xs text-theme-secondary mb-1">Approved Hours</p>
                                        <p className="text-2xl font-bold text-green-600">6.0h</p>
                                    </div>
                                </div>

                                {/* Subject Breakdown */}
                                <div className="mb-6">
                                    <h4 className="font-semibold text-theme-primary mb-3">Subject Breakdown</h4>
                                    <div className="space-y-2">
                                        {[
                                            { subject: 'Physics', hours: 2.5, color: 'bg-blue-500' },
                                            { subject: 'Chemistry', hours: 2.0, color: 'bg-green-500' },
                                            { subject: 'Biology', hours: 2.0, color: 'bg-purple-500' },
                                        ].map((item) => (
                                            <div key={item.subject} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                                                    <span className="text-sm font-medium text-theme-primary">{item.subject}</span>
                                                </div>
                                                <span className="text-sm font-bold text-theme-primary">{item.hours}h</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Tasks Completed */}
                                <div className="mb-6">
                                    <h4 className="font-semibold text-theme-primary mb-3">Tasks Completed</h4>
                                    <div className="space-y-2">
                                        {[
                                            { title: 'Thermodynamics Chapter 4 Reading', subject: 'Physics' },
                                            { title: '50 MCQs from Organic Chemistry', subject: 'Chemistry' },
                                            { title: 'Human Physiology Revision', subject: 'Biology' },
                                        ].map((task, i) => (
                                            <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                                <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-theme-primary">{task.title}</p>
                                                    <p className="text-xs text-theme-secondary">{task.subject}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Mentor Feedback */}
                                <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare className="h-4 w-4 text-amber-500" />
                                        <h4 className="font-semibold text-amber-800 dark:text-amber-200 text-sm">Mentor Feedback</h4>
                                    </div>
                                    <p className="text-sm text-amber-700 dark:text-amber-300/80 italic">
                                        "Good job! Keep it up. Your consistency in daily submissions is improving."
                                    </p>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
